import type { TimelineFrame } from './RecordingManager';
import type { PlaybackController } from './PlaybackController';

/**
 * TimelineRenderer - Canvas-based Animation Timeline Visualization
 * 
 * Renders a multi-lane timeline showing:
 * - 3 lanes: ACTION, ACTIVE, FADING
 * - Phase-colored segments (windup/active/release/recovery)
 * - Weight-based opacity
 * - Blend overlays
 * - State transition markers
 * - Playhead indicator
 */
export class TimelineRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private width: number = 0;
  private height: number = 0;

  // Colors
  private readonly PHASE_COLORS = {
    windup: '#FFD700', // Gold
    active: '#00FF00', // Green
    release: '#FF8C00', // Dark orange
    recovery: '#4169E1', // Royal blue
  };

  private readonly BLEND_COLOR = 'rgba(255, 255, 0, 0.3)'; // Yellow overlay
  private readonly PLAYHEAD_COLOR = '#FF0000'; // Red
  private readonly STATE_MARKER_COLOR = 'rgba(255, 255, 255, 0.6)'; // White dashed lines
  private readonly BACKGROUND_COLOR = '#1a1a1a';
  private readonly GRID_COLOR = 'rgba(255, 255, 255, 0.1)';

  // Layout
  private readonly LANE_HEIGHT = 40;
  private readonly LANE_GAP = 4;
  private readonly MARGIN_TOP = 20;
  private readonly MARGIN_BOTTOM = 30;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get 2D context');
    this.ctx = ctx;
    
    this.resize();
  }

  /**
   * Resize canvas to match its display size
   */
  resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    
    this.width = rect.width;
    this.height = rect.height;
    
    this.canvas.width = this.width * dpr;
    this.canvas.height = this.height * dpr;
    
    this.ctx.scale(dpr, dpr);
  }

  /**
   * Render timeline
   */
  render(frames: TimelineFrame[], playbackController: PlaybackController, scrollingMode: boolean = true): void {
    if (!frames || frames.length === 0) {
      this.renderEmpty();
      return;
    }

    // Clear
    this.ctx.fillStyle = this.BACKGROUND_COLOR;
    this.ctx.fillRect(0, 0, this.width, this.height);

    const playbackState = playbackController.getState();
    const currentTime = scrollingMode ? frames[frames.length - 1].timestamp : playbackState.currentTime;
    const duration = frames[frames.length - 1].timestamp;

    // Calculate visible time window
    const windowDuration = scrollingMode ? duration : duration;
    const startTime = scrollingMode ? Math.max(0, currentTime - windowDuration) : 0;
    const endTime = scrollingMode ? currentTime : duration;

    // Draw time grid
    this.drawTimeGrid(startTime, endTime);

    // Draw lanes
    const laneY = this.MARGIN_TOP;
    this.drawLane('ACTION', frames, startTime, endTime, laneY, 0);
    this.drawLane('ACTIVE', frames, startTime, endTime, laneY + this.LANE_HEIGHT + this.LANE_GAP, 1);
    this.drawLane('FADING', frames, startTime, endTime, laneY + (this.LANE_HEIGHT + this.LANE_GAP) * 2, 2);

    // Draw state transition markers
    this.drawStateMarkers(frames, startTime, endTime);

    // Draw playhead
    if (!scrollingMode) {
      this.drawPlayhead(currentTime, startTime, endTime);
    }

    // Draw time axis
    this.drawTimeAxis(startTime, endTime);
  }

  private renderEmpty(): void {
    this.ctx.fillStyle = this.BACKGROUND_COLOR;
    this.ctx.fillRect(0, 0, this.width, this.height);
    
    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    this.ctx.font = '14px sans-serif';
    this.ctx.textAlign = 'center';
    this.ctx.fillText('No recording data', this.width / 2, this.height / 2);
  }

  private drawTimeGrid(startTime: number, endTime: number): void {
    const duration = endTime - startTime;
    const timeStep = this.calculateTimeStep(duration);
    
    this.ctx.strokeStyle = this.GRID_COLOR;
    this.ctx.lineWidth = 1;

    for (let t = Math.ceil(startTime / timeStep) * timeStep; t <= endTime; t += timeStep) {
      const x = this.timeToX(t, startTime, endTime);
      this.ctx.beginPath();
      this.ctx.moveTo(x, this.MARGIN_TOP);
      this.ctx.lineTo(x, this.height - this.MARGIN_BOTTOM);
      this.ctx.stroke();
    }
  }

  private drawLane(laneName: string, frames: TimelineFrame[], startTime: number, endTime: number, y: number, laneIndex: number): void {
    // Draw lane background
    this.ctx.fillStyle = 'rgba(40, 40, 40, 0.5)';
    this.ctx.fillRect(0, y, this.width, this.LANE_HEIGHT);

    // Draw lane label
    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    this.ctx.font = '11px monospace';
    this.ctx.textAlign = 'left';
    this.ctx.fillText(laneName, 4, y + 12);

    // Draw animation segments
    for (let i = 0; i < frames.length; i++) {
      const frame = frames[i];
      const track = this.getTrackForLane(frame, laneIndex);
      
      if (!track || track.weight === 0) continue;

      const x1 = this.timeToX(frame.timestamp, startTime, endTime);
      const x2 = i < frames.length - 1 
        ? this.timeToX(frames[i + 1].timestamp, startTime, endTime)
        : this.width;

      // Draw phase-colored segment
      const phase = this.getCurrentPhase(track.progress, track.phases);
      const color = this.PHASE_COLORS[phase] || this.PHASE_COLORS.active;
      const alpha = track.weight;
      
      this.ctx.fillStyle = this.hexToRgba(color, alpha);
      this.ctx.fillRect(x1, y + 15, x2 - x1, this.LANE_HEIGHT - 20);

      // Draw animation name (if wide enough)
      if (x2 - x1 > 50) {
        this.ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
        this.ctx.font = '10px sans-serif';
        this.ctx.textAlign = 'left';
        const text = `${track.name} (${(track.weight * 100).toFixed(0)}%)`;
        this.ctx.fillText(text, x1 + 4, y + 28);
      }
    }

    // Draw blend overlays (yellow regions where multiple tracks exist)
    this.drawBlendRegions(frames, startTime, endTime, y);
  }

  private drawBlendRegions(frames: TimelineFrame[], startTime: number, endTime: number, y: number): void {
    for (let i = 0; i < frames.length; i++) {
      const frame = frames[i];
      
      // Check if blending is happening
      if (!frame.state.isBlending) continue;

      const x1 = this.timeToX(frame.timestamp, startTime, endTime);
      const x2 = i < frames.length - 1 
        ? this.timeToX(frames[i + 1].timestamp, startTime, endTime)
        : this.width;

      this.ctx.fillStyle = this.BLEND_COLOR;
      this.ctx.fillRect(x1, y + 15, x2 - x1, this.LANE_HEIGHT - 20);
    }
  }

  private drawStateMarkers(frames: TimelineFrame[], startTime: number, endTime: number): void {
    let lastState = '';
    
    for (const frame of frames) {
      if (frame.currentState !== lastState && lastState !== '') {
        const x = this.timeToX(frame.timestamp, startTime, endTime);
        
        this.ctx.strokeStyle = this.STATE_MARKER_COLOR;
        this.ctx.lineWidth = 1;
        this.ctx.setLineDash([4, 4]);
        this.ctx.beginPath();
        this.ctx.moveTo(x, this.MARGIN_TOP);
        this.ctx.lineTo(x, this.height - this.MARGIN_BOTTOM);
        this.ctx.stroke();
        this.ctx.setLineDash([]);
      }
      lastState = frame.currentState;
    }
  }

  private drawPlayhead(currentTime: number, startTime: number, endTime: number): void {
    const x = this.timeToX(currentTime, startTime, endTime);
    
    this.ctx.strokeStyle = this.PLAYHEAD_COLOR;
    this.ctx.lineWidth = 2;
    this.ctx.beginPath();
    this.ctx.moveTo(x, this.MARGIN_TOP);
    this.ctx.lineTo(x, this.height - this.MARGIN_BOTTOM);
    this.ctx.stroke();
    
    // Draw triangle at top
    this.ctx.fillStyle = this.PLAYHEAD_COLOR;
    this.ctx.beginPath();
    this.ctx.moveTo(x, this.MARGIN_TOP);
    this.ctx.lineTo(x - 6, this.MARGIN_TOP - 8);
    this.ctx.lineTo(x + 6, this.MARGIN_TOP - 8);
    this.ctx.closePath();
    this.ctx.fill();
  }

  private drawTimeAxis(startTime: number, endTime: number): void {
    const duration = endTime - startTime;
    const timeStep = this.calculateTimeStep(duration);
    const y = this.height - this.MARGIN_BOTTOM + 5;
    
    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    this.ctx.font = '10px monospace';
    this.ctx.textAlign = 'center';

    for (let t = Math.ceil(startTime / timeStep) * timeStep; t <= endTime; t += timeStep) {
      const x = this.timeToX(t, startTime, endTime);
      const seconds = (t / 1000).toFixed(2);
      this.ctx.fillText(`${seconds}s`, x, y + 12);
    }
  }

  private timeToX(time: number, startTime: number, endTime: number): number {
    const duration = endTime - startTime;
    if (duration === 0) return 0;
    return ((time - startTime) / duration) * this.width;
  }

  private getTrackForLane(frame: TimelineFrame, laneIndex: number) {
    switch (laneIndex) {
      case 0: return frame.state.action;
      case 1: return frame.state.active;
      case 2: return frame.state.fading;
      default: return null;
    }
  }

  private getCurrentPhase(progress: number, phases: any): 'windup' | 'active' | 'release' | 'recovery' {
    if (!phases) return 'active';
    
    if (progress <= phases.windup?.end) return 'windup';
    if (progress <= phases.active?.end) return 'active';
    if (progress <= phases.release?.end) return 'release';
    return 'recovery';
  }

  private calculateTimeStep(duration: number): number {
    // Aim for ~10-15 grid lines
    const targetSteps = 12;
    const roughStep = duration / targetSteps;
    
    // Round to nice numbers (100ms, 500ms, 1s, 5s, etc.)
    const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
    const normalized = roughStep / magnitude;
    
    if (normalized < 2) return magnitude;
    if (normalized < 5) return magnitude * 2;
    return magnitude * 5;
  }

  private hexToRgba(hex: string, alpha: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
}
